import { eventChannel } from "@redux-saga/core";
import { EditorElementInterfaceBeta, OnPageElementsTypeBeta } from "../../../../../../../ducks/types";
import { ContentElements, ExtractedHeaders } from "./types";

/**
 * Opens an EventSource to the given URL and returns an event channel
 * that emits server-sent events from the EventSource.
 * @param {string} url - The URL to connect the EventSource to
 * @returns {EventChannel} An event channel that emits server-sent events
 */
// tslint:disable-next-line: typedef
export function listenRecommendationsSSEvents(url: string) {
    const eventChan = eventChannel((emit) => {
        const sseSource = new EventSource(url, {
            withCredentials: true
        });
        sseSource.addEventListener("receive_recommendations", (ev: Event) => emit(ev));
        sseSource.addEventListener("receive_recommendation_score", (ev: Event) => emit(ev));
        sseSource.addEventListener("receive_recommendations_busi_comp", (ev: Event) => emit(ev));
        sseSource.addEventListener("receive_recommendations_serp", (ev: Event) => emit(ev));
        sseSource.addEventListener("receive_recommendation_score_busi_comp", (ev: Event) => emit(ev));
        sseSource.addEventListener("receive_recommendation_score_serp", (ev: Event) => emit(ev));
        sseSource.addEventListener("req_completed", (ev: Event) => emit(ev));
        sseSource.addEventListener("req_failed", (ev: Event) => emit(ev));
        sseSource.onerror = (ev: Event) => {
            emit(ev);
        };
        return () => sseSource.close();
    });
    return eventChan;
}

export const extractContentElements = (visibleContent: EditorElementInterfaceBeta[]): ContentElements => {
    const contentElements: ContentElements = {
        "Title": [],
        "Meta Description": [],
        "H1": [],
        "H2": [],
        "H3": [],
        "URL": [],
    };

    visibleContent.forEach((element) => {
        let targetKey: keyof ContentElements | undefined;

        if (element.title === OnPageElementsTypeBeta.TITLE) {
            targetKey = "Title";
        } else if (element.title === OnPageElementsTypeBeta.META_DESCRIPTION) {
            targetKey = "Meta Description";
        } else if (element.title === OnPageElementsTypeBeta.STRIPPED_PAGE) {
            if (typeof element.contents === "string") {
                const extractedHeaders = extractHeadersFromHtml(element.contents);
                contentElements.H1.push(...extractedHeaders.H1);
                contentElements.H2.push(...extractedHeaders.H2);
                contentElements.H3.push(...extractedHeaders.H3);
            }
        } else if (element.title === OnPageElementsTypeBeta.MISSING_H1) {
            targetKey = "H1";
        } else if (element.title === OnPageElementsTypeBeta.MISSING_H2) {
            targetKey = "H2";
        } else if (element.title === OnPageElementsTypeBeta.MISSING_H3) {
            targetKey = "H3";
        } else if (element.title === OnPageElementsTypeBeta.URL) {
            if (element.contents) {
                contentElements.URL.push(element.contents as string);
            }
        }

        if (targetKey && typeof element.contents !== "string") {
            contentElements[targetKey].push(...element.contents);
        }
    });

    return Object.keys(contentElements).reduce((acc, key) => {
        if (contentElements[key as keyof ContentElements].length > 0) {
            acc[key as keyof ContentElements] = contentElements[key as keyof ContentElements];
        }
        return acc;
    }, {} as ContentElements);
};

export const extractHeadersFromHtml = (htmlString: string): ExtractedHeaders => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const extractTagContent = (tagName: string): string[] => {
        const elements = doc.querySelectorAll(tagName);
        return Array.from(elements).map((element: Element) => element.textContent || "");
    };

    return {
        H1: extractTagContent("h1"),
        H2: extractTagContent("h2"),
        H3: extractTagContent("h3"),
    };
};
